import { PropType, VNode, defineComponent, h } from "vue";
import { NDataTable, PaginationInfo, NDescriptions, NDescriptionsItem, NTag } from 'naive-ui';
import { cloneDeep } from 'lodash';
import { mapStores } from 'pinia';
import moment from 'moment';

import { useAuthStore } from '~/store/auth.store';

import { Measure } from "~/api/models/measure.model";
import { Station } from "~/api/models/station.model";
import componentBase, { Breakpoint } from '~/components/component-base';
import { FilterOption, FilterOptionValue, FilterState, SortState, TableColumn } from "naive-ui/es/data-table/src/interface";

export type MeasureColumn = TableColumn<Row> & {
    enabled: () => boolean;
    breakpoints?: Breakpoint[];
}

export interface Row {
    key: string | number,
    date: number;
    type: string;
    lel: number;
    battery: string;
    humidity: number;
    runtime: number;
    temperature: number;

    CH4_system_failure: boolean;

    rssi: number,
    snr: number,
    spreading_factor: number,

    // LTEM radio
    rsrq:  number,
    rsrp:  number,

    CH4_concentration_alarm: number;
    CH4_sensor_error_alarm: number;
    CH4_battery_alarm: number;
    CH4_temperature_alarm: number;
    CH4_detector_error: number;
    CH4_ndir_error: number;

    //payload v3
    CH4_ndir_fatal_alarm: number;
    CH4_ndir_intermittent_alarm: number;
    CH4_ndir_communication_alarm: number;
    CH4_env_out_of_range_alarm: number;
    CH4_internal_alarm: number;
    CH4_eol_alarm: number;
}

export default defineComponent({

    extends: componentBase,

    components: {
        NDataTable,
    },

    props: {
        measures: { type: Array as PropType<Measure[]>, default: () => []},
        station: { type: Object as PropType<Station>},
        },
        
        data() {
            return {
                loading: false,
                allColumns: [
                    {
                        key: 'expand-message',
                        type: 'expand',
                        breakpoints: [ "mobile", "tablet", "desktop", "fullhd", "widescreen"],
                        renderExpand: (message: Row) => h('div', { class: 'p-2'}, { default: () => [
                            h(
                                NDescriptions, 
                                { 
                                    title: this.$t('details.messages_datatable.columns.expand.title') , 
                                    'label-placement': "top", 
                                    class: 'w-100 mb-3', 
                                    columns: ['mobile', 'tablet'].includes(this.breakpoint) ? 2 : 4
                                }, 
                                { 
                                    default: () => {
                                        const duration = moment.duration(message.runtime, 'hours');
                                        const months = duration.months()
                                        const days = duration.days();
                                        const hours = duration.hours();
                                        const runtime =  `${months ? this.$t('time.n-month', months): ''}  ${days ? this.$t('time.n-day', days): ''} ${this.$t('time.n-hour', hours)}`;
                                        const base = [
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.date.label'), default: () => moment.unix(message.date).format('LLL')}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.lel.label'), default: () => message.lel}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.temperature.label'), default: () => message.temperature}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.humidity.label'), default: () => message.humidity}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.runtime.label'), default: () => runtime}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.battery.label'), default: () => message.battery}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.expand.sensor_status.label'), default: () => this.getSensorStatus(message)}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.ch4_alarm.label'), default: () => this.getAlarmFlagStatus(message.CH4_concentration_alarm, 'error')}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.battery_alarm.label'), default: () => this.getAlarmFlagStatus(message.CH4_battery_alarm, 'error')}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.system_failure.label'), default: () => this.getAlarmFlagStatus(message.CH4_system_failure, 'warning')}),
                                            h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.end_of_life_alarm.label'), default: () => this.getAlarmFlagStatus(message.CH4_eol_alarm, 'warning')}),
                                        ];

                                        if(this.isLTEMStation(this.station)) {
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.rssi.label'), default: () => message.rssi}));
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.rsrp.label'), default: () => message.rsrp}));
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.rsrq.label'), default: () => message.rsrq}));
                                        }

                                        if(this.isLoRaWanStation(this.station)) {
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.rssi.label'), default: () => message.rssi}));
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.spreading_factor.label'), default: () => message.spreading_factor}));
                                            base.push(h(NDescriptionsItem, {}, { label: () => this.$t('details.messages_datatable.columns.snr.label'), default: () => message.snr}));
                                        }

                                        return base;
                                    }
                                }
                            ),
                    ]}),
                },
                {
                    key: 'date',
                    title: this.$t('details.messages_datatable.columns.date.label'),
                    breakpoints: [ "mobile", "tablet", "desktop", "fullhd", "widescreen"],
                    sorter: 'default',
                    render: (message) => moment.unix(message.date).format('LLL'),
                },
                {
                    title: this.$t('details.messages_datatable.columns.type.label'),
                    key: 'type',
                    filterOptions: [
                        { label: this.$t('details.messages_datatable.columns.type.payload_type_alert'), value: 'ALARM'} as FilterOption,
                        { label: this.$t('details.messages_datatable.columns.type.payload_type_heartbeat'), value: 'HEARTBEAT'} as FilterOption,
                    ],
                    filter: (type: string, measure: Row) => measure.type === type,
                    render: (measure: Row) => {
                        switch (measure.type) {
                            case 'ALARM':
                                return this.$t('details.messages_datatable.columns.type.payload_type_alert')
                            case 'HEARTBEAT':
                                return this.$t('details.messages_datatable.columns.type.payload_type_heartbeat')
                            default:
                                return '-'
                        }
                    }
                },
                {
                    key: 'lel',
                    title: this.$t('details.messages_datatable.columns.lel.label'),
                    enabled: () => !this.isAvolta50(this.station)
                },
                {
                    key: 'ppm',
                    title: this.$t('details.messages_datatable.columns.ppm.label'),
                    enabled: () => this.isAvolta50(this.station)
                },
                {
                    key: 'temperature',
                    title: this.$t('details.messages_datatable.columns.temperature.label'),
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },
                {
                    key: 'humidity',
                    title: this.$t('details.messages_datatable.columns.humidity.label'),
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },

                {
                    key: 'battery',
                    title: this.$t('details.messages_datatable.columns.battery.label'),
                    breakpoints: [ "fullhd", "widescreen"]
                },

                {
                    key: 'CH4_concentration_alarm',
                    title: this.$t('details.messages_datatable.columns.ch4_alarm.label'),
                    render: (row: Row) => {
                        return h('span', { class: `mdi has-text-white ${row.CH4_concentration_alarm ? 'mdi-alert' : 'mdi-minus'}` });
                    },
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },
                {
                    key: 'CH4_system_failure_alarm',
                    title: this.$t('details.messages_datatable.columns.system_failure.label'),
                    render: (row: Row) => {
                        const rendered = [h('span', { class: `mdi has-text-white ${row.CH4_system_failure ? 'mdi-alert' : 'mdi-minus'}` })];
                        return rendered;
                    },
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },
                {
                    key: 'CH4_battery_alarm',
                    title: this.$t('details.messages_datatable.columns.battery_alarm.label'),
                    render: (row: Row) => {
                        return h('span', { class: `mdi has-text-white ${row.CH4_battery_alarm ? 'mdi-alert' : 'mdi-minus'}` });
                    },
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },
                {
                    key: 'CH4_eol_alarm',
                    title: this.$t('details.messages_datatable.columns.end_of_life_alarm.label'),
                    render: (row: Row) => {
                        return h('span', { class: `mdi has-text-white ${row.CH4_eol_alarm ? 'mdi-alert' : 'mdi-minus'}` });
                    },
                    breakpoints: [ "desktop", "fullhd", "widescreen"]
                },
                {
                    key: 'rssi',
                    title: this.$t('details.messages_datatable.columns.rssi.label') + ' (dBm)',
                    breakpoints: ["fullhd", "widescreen"]
                },
                {
                    key: 'snr',
                    title: this.$t('details.messages_datatable.columns.snr.label'),
                    enabled: () => this.isLoRaWanStation(this.station),
                    breakpoints: ["fullhd", "widescreen"]
                },
                {
                    key: 'spreading_factor',
                    title: this.$t('details.messages_datatable.columns.spreading_factor.label'),
                    enabled: () => this.isLoRaWanStation(this.station),
                    breakpoints: ["fullhd", "widescreen"]
                },

                {
                    key: 'rsrp',
                    title: this.$t('details.messages_datatable.columns.rsrp.label') + ' (dBm)',
                    enabled: () => this.isLTEMStation(this.station),
                    breakpoints: ["fullhd", "widescreen"]
                },
                {
                    key: 'rsrq',
                    title: this.$t('details.messages_datatable.columns.rsrq.label') + ' (dB)',
                    enabled: () => this.isLTEMStation(this.station),
                    breakpoints: ["fullhd", "widescreen"]
                },

                {
                    key: 'runtime',
                    title: this.$t('details.messages_datatable.columns.runtime.label'),
                    breakpoints: ["fullhd", "widescreen"],
                    enabled: () => !this.isAvolta50(this.station),
                    render: (message: Row) => {
                        if(message.runtime !== undefined) {
                            const duration = moment.duration(message.runtime, 'hours');
                            const months = duration.months()
                            const days = duration.days();
                            const hours = duration.hours();
                            return `${months ? this.$t('time.n-month', months): ''}  ${days ? this.$t('time.n-day', days): ''} ${this.$t('time.n-hour', hours)}`;
                        } else {
                            return '-'
                        }
                    }
                },
            ] as MeasureColumn [],
            pagination: { 
                page: 1,
                pageCount: Math.floor(this.measures?.length || 0) / 10 + 1,
                pageSize: 20,        
            } as PaginationInfo,

            filter: null as FilterState | null,
            sorter: null as SortState | null,
            data: [],
        };

       
    },

    mounted() {
        this.query(1, this.pagination.pageSize);
    },

    computed: {
        ...mapStores(useAuthStore),

        pn(): string {
            return this.station?.hardware;
        },

        columns(): MeasureColumn[] {
            return this.allColumns
                .filter(column => !column?.breakpoints || column?.breakpoints?.includes(this.breakpoint))
                .filter((column) => !column.enabled || column.enabled());
        },

        formattedMessages(): Row[] {
            if(!this.measures) {
                return [];
            }

            return this.measures.map((measure: Measure) => {
                    return {
                        key: measure.date,
                        date: measure.date,
                        type: measure?.values?.type as string,
                        lel: measure?.values?.CH4_lel,
                        ppm: measure?.values?.CH4,
                        battery: measure?.values?.battery ? (measure?.values?.battery / 1000).toFixed(1): '-',
                        humidity: measure?.values?.humidity as number,
                        runtime: measure?.values?.run_time as number,
                        temperature: measure?.values?.temperature as number,

                        CH4_system_failure: this.hasGlobalError(measure),

                        CH4_concentration_alarm: measure?.values?.CH4_concentration_alarm as number,
                        CH4_battery_alarm: measure?.values?.CH4_battery_alarm as number,
                        CH4_temperature_alarm: measure?.values?.CH4_temperature_alarm as number,
                        CH4_sensor_error_alarm: measure?.values?.CH4_sensor_error_alarm as number,

                        CH4_detector_error: measure?.values?.CH4_detector_error as number,
                        CH4_ndir_error: measure?.values?.CH4_ndir_error as number,

                        // LoraWan radio
                        rssi: measure?.rawValues?.rssi as number || measure?.values?.rssi as number,
                        snr: measure?.rawValues?.snr as number,
                        spreading_factor: measure?.rawValues?.spreading_factor as number,

                        // LTEM radio
                        rsrq:  measure?.values?.rsrq as number,
                        rsrp:  measure?.values?.rsrp as number,

                        // payload v3
                        CH4_ndir_fatal_alarm: measure?.values?.CH4_ndir_fatal_alarm  as number,
                        CH4_ndir_intermittent_alarm: measure?.values?.CH4_ndir_intermittent_alarm  as number,
                        CH4_ndir_communication_alarm: measure?.values?.CH4_ndir_communication_alarm  as number,
                        CH4_env_out_of_range_alarm: measure?.values?.CH4_env_out_of_range_alarm as number,
                        CH4_internal_alarm: measure?.values?.CH4_internal_alarm as number,
                        CH4_eol_alarm: measure?.values?.CH4_eol_alarm as number,
                    } as Row;
                });
        },
    },

    methods: {
        goToStationDetails(station: Station): void {
            this.$router.push({ name: 'station-details', params: { station: station.id } })
        },

        handleSorterChange(sortState: SortState) {
            this.sorter = sortState;
            this.query(1, this.pagination.pageSize);
        },

        handleFiltersChange(filterState: FilterState) {
            this.filter = filterState;
            this.query(1, this.pagination.pageSize);
        },

        handlePageChange(page) {
            this.query(page, this.pagination.pageSize);
        },

        hasGlobalError(message: Measure): boolean {
            const status: boolean [] = [
                !!message?.values?.CH4_env_out_of_range_alarm,
                !!message?.values?.CH4_internal_alarm,
                !!message?.values?.CH4_ndir_communication_alarm,
                !!message?.values?.CH4_ndir_fatal_alarm,
                !!message?.values?.CH4_ndir_intermittent_alarm,
                !!message?.values?.CH4_sensor_error_alarm,
            ];
            return status.some(s => s);
        },

        getSensorStatus(message: Row): VNode {
            if(message.CH4_ndir_fatal_alarm) {
                return h(NTag, { type: "error", size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.fatal')});
            } else if (message.CH4_ndir_intermittent_alarm) {
                return h(NTag, { type: "warning", size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.intermittent')});
            } else if (message.CH4_ndir_communication_alarm) {
                return h(NTag, { type: "warning", size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.communication_failure')});
            }  else if (message.CH4_env_out_of_range_alarm) {
                return h(NTag, { type: 'warning', size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.env_out_of_range')});
            } else {
                return h(NTag, { type: 'success', size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.ok')});
            }
        },

        getAlarmFlagStatus(flag: boolean, type: 'error'|'warning' = 'error'): VNode {
            if(flag) {
                const content: string = type === 'error' ? this.$t('details.messages_datatable.columns.expand.alarm_flag_error') : this.$t('details.messages_datatable.columns.expand.alarm_flag_warning');
                return h(NTag, { type, size: 'small', bordered: false}, { default: () => content});
            }else {
                return h(NTag, { type: 'success', size: 'small', bordered: false}, { default: () => this.$t('details.messages_datatable.columns.expand.sensor_status.ok')});
            }
        },

        query(page: number, pageSize = 10) {
            const skip = (page - 1) * pageSize;
            const top = pageSize;

            let rows: Row[] = cloneDeep(this.formattedMessages);

            if(this.filter && (this.filter.type as FilterOptionValue[]).length) {
                rows = rows.filter((row) => (this.filter.type as FilterOptionValue[]).includes(row.type));
            }

            rows = rows.sort((m1, m2) => m2.date - m1.date);

            if(this.sorter && this.sorter.columnKey === 'date' && this.sorter.order === 'descend') {
                rows = rows.sort().reverse();
            }

            this.pagination.pageCount = Math.ceil(rows.length / this.pagination.pageSize);
            rows = rows.slice(skip, skip + top);

            this.data = rows;

            this.pagination.page = page;
        }
    },

    watch: {
        measures() {
            this.query(1, this.pagination.pageSize);
        },
    }
})